import React, { useEffect } from 'react';
import './PrivacyPage.css';

const PrivacyPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="privacy-page">
      <div className="privacy-container">
        <div className="privacy-header">
          <div className="privacy-header-spacer" />
          <h1 className="privacy-title">Privacy Policy</h1>
          <p className="privacy-effective-date">Effective Date: November 13, 2025</p>
        </div>

        <div className="privacy-content">
          <section className="privacy-section">
            <h2 className="section-title">1. Introduction</h2>
            <p>
              Playlounge LLC ("Company", "we", "us", or "our"), operates the Novembr platform 
              and services (the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you use our Service.
            </p>
            <p>
              Please read this Privacy Policy carefully. By accessing or using the Service, you agree to the collection and use 
              of information in accordance with this Privacy Policy. If you do not agree with our policies and practices, do not 
              use the Service.
            </p>
          </section>

          <section className="privacy-section">
            <h2 className="section-title">2. Information We Collect</h2>
            
            <h3 className="subsection-title">2.1 Information You Provide to Us</h3>
            <p>We collect information that you provide directly to us, including:</p>
            <ul>
              <li><strong>Account Information:</strong> When you register for an account, we collect your email address, name, 
              and any other information you choose to provide.</li>
              <li><strong>API Keys:</strong> We generate and store API keys associated with your account for authentication purposes.</li>
              <li><strong>Payment Information:</strong> When you provide payment information, it is processed by our third-party 
              payment processor, Stripe, Inc. We do not store your full payment card details, but we may store payment method 
              references and billing information.</li>
              <li><strong>Billing Information:</strong> We collect billing addresses and other information necessary for processing 
              payments and generating invoices.</li>
              <li><strong>Communications:</strong> If you contact us, we may collect your name, email address, and the content of 
              your communications.</li>
            </ul>

            <h3 className="subsection-title">2.2 Information We Collect Automatically</h3>
            <p>When you use the Service, we automatically collect certain information, including:</p>
            <ul>
              <li><strong>Usage Data:</strong> We collect information about your use of the Service, including API endpoints accessed, 
              request methods, response times, request and response sizes, status codes, and timestamps.</li>
              <li><strong>Technical Data:</strong> We collect your IP address, user agent, browser type, operating system, and other 
              technical information about your device and how you access the Service.</li>
              <li><strong>API Request Data:</strong> We log metadata about API requests, including endpoint names, methods, status 
              codes, response times, and associated costs.</li>
              <li><strong>Account Activity:</strong> We track your account activity, including login times, API key usage, and billing 
              activity.</li>
            </ul>

            <h3 className="subsection-title">2.3 Data You Submit Through the Service</h3>
            <p>
              When you use our API services, you may submit images, text, or other content for analysis. We process this data 
              solely to provide the Service to you. We do not use this data for any other purpose without your explicit consent.
            </p>
          </section>

          <section className="privacy-section">
            <h2 className="section-title">3. How We Use Your Information</h2>
            <p>We use the information we collect for the following purposes:</p>
            <ul>
              <li><strong>To Provide and Maintain the Service:</strong> We use your information to create and manage your account, 
              authenticate API requests, process your API calls, and provide customer support.</li>
              <li><strong>To Process Payments:</strong> We use payment information to process transactions, manage billing, and prevent 
              fraud. Payment processing is handled by Stripe, Inc., and your use of Stripe's services is subject to Stripe's 
              privacy policy.</li>
              <li><strong>To Monitor Usage and Billing:</strong> We track API usage to calculate costs, generate invoices, enforce 
              spending limits, and provide usage analytics.</li>
              <li><strong>To Improve the Service:</strong> We analyze usage patterns and technical data to improve the performance, 
              reliability, and features of the Service.</li>
              <li><strong>To Communicate with You:</strong> We use your contact information to send you service-related notifications, 
              billing information, security alerts, and respond to your inquiries.</li>
              <li><strong>To Ensure Security:</strong> We use information to detect, prevent, and address security issues, fraud, and 
              unauthorized access to the Service.</li>
              <li><strong>To Comply with Legal Obligations:</strong> We may use your information to comply with applicable laws, 
              regulations, legal processes, or governmental requests.</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2 className="section-title">4. How We Share Your Information</h2>
            <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
            
            <h3 className="subsection-title">4.1 Service Providers</h3>
            <p>We share information with third-party service providers who perform services on our behalf:</p>
            <ul>
              <li><strong>Google Firebase:</strong> We use Firebase for authentication, database services, and user management.
              Your account information and usage data are stored on Firebase infrastructure.</li>
              <li><strong>Stripe, Inc.:</strong> We use Stripe to process payments. Stripe receives payment information and billing 
              data necessary to process transactions. Stripe's use of your information is governed by Stripe's privacy policy.</li>
              <li><strong>Hosting and Infrastructure Providers:</strong> We may use cloud hosting providers to host and operate the 
              Service. These providers may have access to your information as necessary to provide their services.</li>
            </ul>

            <h3 className="subsection-title">4.2 Legal Requirements</h3>
            <p>We may disclose your information if required to do so by law or in response to valid requests by public authorities 
            (e.g., a court or government agency).</p>

            <h3 className="subsection-title">4.3 Business Transfers</h3>
            <p>If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that 
            transaction. We will notify you of any such change in ownership or control of your personal information.</p>

            <h3 className="subsection-title">4.4 With Your Consent</h3>
            <p>We may share your information with your explicit consent or at your direction.</p>
          </section>

          <section className="privacy-section">
            <h2 className="section-title">5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect your information against unauthorized 
              access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul>
              <li>Encryption of data in transit using TLS/SSL</li>
              <li>Secure storage of API keys and authentication credentials</li>
              <li>Access controls and authentication requirements</li>
              <li>Regular security assessments and monitoring</li>
              <li>Secure payment processing through Stripe</li>
            </ul>
            <p>
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use 
              commercially acceptable means to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          <section className="privacy-section">
            <h2 className="section-title">6. Data Retention</h2>
            <p>
              We retain your information for as long as necessary to provide the Service, comply with legal obligations, resolve 
              disputes, and enforce our agreements. Specifically:
            </p>
            <ul>
              <li><strong>Account Information:</strong> We retain your account information for as long as your account is active 
              and for a reasonable period thereafter to comply with legal obligations and resolve disputes.</li>
              <li><strong>Usage Data:</strong> We retain API usage logs and analytics data for billing, analytics, and service 
              improvement purposes. Usage data may be retained for up to 7 years for accounting and legal compliance purposes.</li>
              <li><strong>Payment Information:</strong> Payment and billing information is retained as required by law and for 
              accounting purposes, typically for 7 years.</li>
              <li><strong>API Request Data:</strong> Metadata about API requests is retained for billing, analytics, and security 
              purposes. We do not retain the actual content of API requests (images, text) beyond what is necessary to process the 
              request, unless you explicitly request otherwise.</li>
            </ul>
            <p>
              When you delete your account, we will delete or anonymize your personal information, except where we are required 
              to retain it for legal or legitimate business purposes.
            </p>
          </section>

          <section className="privacy-section">
            <h2 className="section-title">7. Your Rights and Choices</h2>
            <p>Depending on your location, you may have certain rights regarding your personal information:</p>
            
            <h3 className="subsection-title">7.1 Access and Portability</h3>
            <p>You have the right to access the personal information we hold about you and to receive a copy of your data in a 
            portable format.</p>

            <h3 className="subsection-title">7.2 Correction</h3>
            <p>You can update your account information at any time through your account settings or by contacting us.</p>

            <h3 className="subsection-title">7.3 Deletion</h3>
            <p>You may request deletion of your account and personal information by contacting us at teamdecembr@gmail.com. 
            We will delete your information subject to our legal obligations to retain certain data.</p>

            <h3 className="subsection-title">7.4 Opt-Out of Communications</h3>
            <p>You can opt out of marketing communications by following the unsubscribe instructions in our emails or by contacting us. 
            You may not opt out of service-related communications (e.g., billing, security alerts).</p>

            <h3 className="subsection-title">7.5 Data Processing Objection</h3>
            <p>You may object to certain processing of your personal information. However, we may need to continue processing your 
            information to provide the Service or comply with legal obligations.</p>

            <h3 className="subsection-title">7.6 California Privacy Rights</h3>
            <p>If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA), 
            including the right to know what personal information we collect, the right to delete personal information, and the 
            right to opt out of the sale of personal information (we do not sell personal information).</p>

            <p>
              To exercise any of these rights, please contact us at teamdecembr@gmail.com. We will respond to your request within 
              a reasonable timeframe and in accordance with applicable law.
            </p>
          </section>

          <section className="privacy-section">
            <h2 className="section-title">8. Children's Privacy</h2>
            <p>
              The Service is not intended for individuals under the age of 18. We do not knowingly collect personal information 
              from children under 18. If you are a parent or guardian and believe your child has provided us with personal information, 
              please contact us immediately. If we become aware that we have collected personal information from a child under 18, 
              we will take steps to delete such information.
            </p>
          </section>

          <section className="privacy-section">
            <h2 className="section-title">9. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence. These countries 
              may have data protection laws that differ from those in your country. By using the Service, you consent to the 
              transfer of your information to the United States and other countries where we operate.
            </p>
            <p>
              We take appropriate safeguards to ensure that your information receives an adequate level of protection in the 
              jurisdictions in which we process it.
            </p>
          </section>

          <section className="privacy-section">
            <h2 className="section-title">10. Third-Party Services</h2>
            <p>
              The Service may contain links to third-party websites or services. We are not responsible for the privacy practices 
              of these third parties. We encourage you to read the privacy policies of any third-party services you access.
            </p>
            <p>
              Our use of third-party services is subject to their respective privacy policies:
            </p>
            <ul>
              <li><strong>Firebase:</strong> <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">https://firebase.google.com/support/privacy</a></li>
              <li><strong>Stripe:</strong> <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">https://stripe.com/privacy</a></li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2 className="section-title">11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new 
              Privacy Policy on this page and updating the "Effective Date" at the top of this Privacy Policy.
            </p>
            <p>
              Your continued use of the Service after any changes to this Privacy Policy constitutes your acceptance of the updated 
              Privacy Policy. We encourage you to review this Privacy Policy periodically.
            </p>
          </section>

          <section className="privacy-section">
            <h2 className="section-title">12. Contact Us</h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:
            </p>
            <div className="contact-info">
              <p>Novembr</p>
              <p>Email: contact@novembr.dev</p>
            </div>
            <p>
              For privacy-related inquiries, please include "Privacy Policy" in the subject line of your email.
            </p>
          </section>
        </div>
      </div>
    </section>
  );
};

export default PrivacyPage;

